import { CliError, CliErrorCode } from '@wix/cli-error';
import type { IHttpClient, Headers } from '@wix/http-client';
import { createHttpClient as createHttpClientOriginal } from '@wix/http-client';
import { HttpsProxyAgent } from 'https-proxy-agent';
import { getProxyForUrl } from 'proxy-from-env';
import { isHttpError } from './is-http-error.js';

export interface CreateHttpClientOptions {
  getAppToken?: () => Promise<string>;
  createHeaders?: () => Headers;
  cookies?: Readonly<Record<string, string>>;

  type: keyof typeof baseUrls;
}

const baseUrls = {
  backoffice: 'https://manage.wix.com',
  editor: 'https://editor.wix.com',
  code: 'https://code.wix.com',
  general: 'https://www.wix.com',
  api: 'https://www.wixapis.com',
  standalone: undefined,
};

// Axios 0.21's own env-var proxy support sends HTTPS requests in plain
// absolute-form instead of opening a CONNECT tunnel, so strict forward
// proxies reject them with a 4xx. HttpsProxyAgent tunnels properly.
function createProxyAgent(baseUrl: string | undefined) {
  // getProxyForUrl answers "which proxy should this URL go through?" using
  // the standard env-var conventions (same as axios).
  // It returns '' when the host should connect directly.
  const proxyUrl = baseUrl
    ? getProxyForUrl(baseUrl)
    : (process.env.HTTPS_PROXY ?? process.env.https_proxy ?? '');

  return proxyUrl ? new HttpsProxyAgent(proxyUrl) : undefined;
}

function interceptRequest<P extends unknown[], R>(
  client: IHttpClient,
  fn: (...args: P) => Promise<R>
) {
  return (...args: P) =>
    fn.apply(client, args).catch((error: unknown) => {
      if (isHttpError(error) && !client.isCancel(error) && !error.response) {
        // Network error
        throw new CliError({
          code: CliErrorCode.NetworkError(),
          cause: error,
          info: { code: error.code },
        });
      }

      throw error;
    });
}

export function createHttpClient({
  getAppToken,
  createHeaders,
  cookies,
  type,
}: CreateHttpClientOptions): IHttpClient {
  const cookieHeader = Object.entries({ 'XSRF-TOKEN': 'nocheck', ...cookies })
    .map(([name, value]) => `${name}=${value}`)
    .join('; ');
  const client = createHttpClientOriginal({
    baseURL: baseUrls[type],
    ...(type !== 'standalone'
      ? {
          getAppToken,
        }
      : {}),
    createHeaders: () => ({
      ...createHeaders?.(),
      // See https://wix.slack.com/archives/C0JLGMT28/p1667219337530909
      'X-XSRF-TOKEN': 'nocheck',
      Cookie: cookieHeader,
      'User-Agent': 'wix-cli',
    }),

    proxy: false,
    httpsAgent: createProxyAgent(baseUrls[type]),

    // https://github.com/axios/axios/issues/4263 - fixed in Axios ^1.x.x but currently cannot be bumped in Wix Http Client
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
  });

  return {
    ...client,
    request: interceptRequest(client, client.request),
    get: interceptRequest(client, client.get),
    delete: interceptRequest(client, client.delete),
    head: interceptRequest(client, client.head),
    options: interceptRequest(client, client.options),
    post: interceptRequest(client, client.post),
    put: interceptRequest(client, client.put),
    patch: interceptRequest(client, client.patch),
  };
}
